/**
 * Kreator — podgląd MENU tenanta "na żywo" (makieta, bez sieci).
 *
 * Renderuje roboczą hierarchię menu (`MenuItem[]`) budowaną w `MenuBuilderEmbedded`
 * tak, jak zobaczy ją klient w lewym sidebarze po instalacji modułu — ale statycznie,
 * bez routera, subskrypcji ani zapytań. To makieta wierności wizualnej, nie działający
 * sidebar: pozycje nie nawigują (zwykłe `<div>`/`<span>`, nie `<NavLink>`).
 *
 * Skąd metadane (etykieta/ikona) bez sieci: builder ma już `MenuSourceModule[]`
 * zbudowane z encji szablonu (`<templateSlug>_<entitySlug>` → nazwa + ikona), więc
 * moduł-pozycję rozwiązujemy z tej listy. Pozycje systemowe z `SYSTEM_ITEMS`.
 *
 * Ograniczenia makiety (świadome — te byty zależą od stanu KLIENTA, nie szablonu):
 *  - `installed_area` — zawartość (rodzaje danych z zainstalowanych modułów obszaru)
 *    zależy od tego co klient ma opłacone; pokazujemy nagłówek + notkę.
 *  - `module` — zawsze renderowany jako aktywny (to encje TEGO szablonu; po instalacji
 *    będą aktywne). Wygaszanie „nieaktywnych" (jak w realnym sidebarze) nie dotyczy podglądu.
 *  - landing overrides / dedup PINNED_ROUTES — liczone przez backend przy instalacji;
 *    podgląd pokazuje surową hierarchię roboczą.
 *
 * @see apps/admin/src/components/Sidebar.tsx — realny renderer (RenderMenuItem) u klienta.
 */

import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type MenuItem, type MenuSourceModule, SYSTEM_ITEMS } from '@saas/shared';
import { ExternalLink } from 'lucide-react';

import { PINNED_BASE_ITEM_KEYS, resolveIcon, resolveSectionIcon } from '@/components/Sidebar';
import { tenantSidebarItemClass } from '@/components/sidebar-collapse';

const SYSTEM_ITEM_BY_KEY = new Map(SYSTEM_ITEMS.map((s) => [s.itemKey, s]));

/** Klasa wiersza pozycji — ta sama co w realnym sidebarze tenanta (aktywna = pigułka). */
const ITEM_ROW = tenantSidebarItemClass(false, false);
const ITEM_ROW_ACTIVE = tenantSidebarItemClass(false, true);

/** Klasa nagłówka sekcji / obszaru — jak nagłówek „GŁÓWNE" realnego sidebara tenanta. */
const SECTION_HEADER =
  'pb-[6px] pl-3 pt-3 text-[12px] font-semibold uppercase leading-[16px] tracking-[0.6px] text-[#7b7b8b]';

interface PreviewNodeProps {
  item: MenuItem;
  modulesByCode: Map<string, MenuSourceModule>;
  /** Zunifikowany podgląd: id zaznaczonej pozycji (steruje prawym panelem). */
  selectedId?: string | null;
  /** Zunifikowany podgląd: klik pozycji. Brak = makieta statyczna (jak dotąd). */
  onSelectItem?: (item: MenuItem) => void;
}

function PreviewNode({ item, modulesByCode, selectedId, onSelectItem }: PreviewNodeProps) {
  const { t } = useTranslation();

  if (item.type === 'separator') {
    return <hr className="my-2 border-border-secondary" />;
  }

  if (item.type === 'section') {
    const SectionIcon = resolveSectionIcon(item.iconName);
    return (
      <div className="mt-3">
        <div className={`${SECTION_HEADER} flex items-center gap-1.5`}>
          {SectionIcon && <SectionIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />}
          <span className="min-w-0 truncate">{item.label ?? '—'}</span>
        </div>
        {item.children?.map((child) => (
          <PreviewNode
            key={child.id}
            item={child}
            modulesByCode={modulesByCode}
            selectedId={selectedId}
            onSelectItem={onSelectItem}
          />
        ))}
      </div>
    );
  }

  if (item.type === 'installed_area') {
    return (
      <div className="mt-3">
        <div className={SECTION_HEADER}>
          {item.label ?? t('admin.menuLayoutBuilder.preview.installedAreaTitle')}
        </div>
        <div className="px-4 py-1.5 text-xs italic text-text-muted">
          {t('admin.menuLayoutBuilder.preview.installedAreaNote')}
        </div>
      </div>
    );
  }

  // Wiersz-liść (kartoteka / pozycja systemowa / link). W trybie zunifikowanego
  // podglądu (onSelectItem) wiersz jest przyciskiem sterującym prawym panelem,
  // a zaznaczenie wygląda jak aktywna pozycja realnego sidebara klienta.
  const leafRow = (content: ReactNode) => {
    if (!onSelectItem) return <div className={ITEM_ROW}>{content}</div>;
    const selected = selectedId === item.id;
    return (
      <button
        type="button"
        data-testid={`menu-preview-item-${item.id}`}
        aria-pressed={selected}
        aria-current={selected ? 'page' : undefined}
        onClick={() => onSelectItem(item)}
        className={`${selected ? ITEM_ROW_ACTIVE : ITEM_ROW} w-full cursor-pointer text-left`}
      >
        {content}
      </button>
    );
  };

  if (item.type === 'module') {
    const code = item.moduleCode ?? '';
    const mod = modulesByCode.get(code);
    const Icon = resolveIcon(item.iconName ?? mod?.iconName, undefined);
    const label = item.label ?? mod?.label ?? code;
    return leafRow(
      <>
        <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        <span className="truncate">{label}</span>
      </>,
    );
  }

  if (item.type === 'system_item') {
    const def = item.itemKey ? SYSTEM_ITEM_BY_KEY.get(item.itemKey) : undefined;
    if (!def) return null;
    const Icon = resolveIcon(item.iconName ?? def.iconName, undefined);
    const label = item.label ?? t(def.labelI18nKey, def.defaultLabel);
    return leafRow(
      <>
        <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        <span className="truncate">{label}</span>
      </>,
    );
  }

  if (item.type === 'custom_link') {
    const url = item.url ?? '#';
    const label = item.label ?? url;
    const Icon = resolveIcon(item.iconName, undefined);
    return leafRow(
      <>
        <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        <span className="truncate">{label}</span>
        {item.external && (
          <ExternalLink className="h-3 w-3 flex-shrink-0 text-text-muted" aria-hidden="true" />
        )}
      </>,
    );
  }

  return null;
}

export interface MenuPreviewSidebarProps {
  hierarchy: MenuItem[];
  /** Metadane modułów (etykieta/ikona) — z `source.modules` buildera; bez sieci. */
  modules: MenuSourceModule[];
  /** Nazwa menu — pokazywana pod marką „Workflow365" (jak u klienta). */
  moduleName?: string;
  /** Zunifikowany podgląd: id zaznaczonej pozycji (steruje prawym panelem). */
  selectedId?: string | null;
  /** Zunifikowany podgląd: klik pozycji-liścia. Brak = makieta statyczna. */
  onSelectItem?: (item: MenuItem) => void;
  /** Zunifikowany podgląd: chowa notkę nad sidebar-em (opis żyje w modalu). */
  hideNote?: boolean;
  /**
   * Renderuje bazę stałą (Użytkownicy / Firmy / Ustawienia) pod menu modułu —
   * jak zona 3 realnego sidebara klienta (`PinnedBaseNav`). Pozycje są klikalne
   * w trybie zunifikowanym (id `pinned-<key>`); prawy panel pokazuje notkę
   * „element stały" zamiast zawartości.
   */
  showPinnedBase?: boolean;
  /**
   * Rozciąga makietę na pełną wysokość kontenera (jak panel sidebara u tenanta).
   */
  fullHeight?: boolean;
}

/** Prefiks id syntetycznych pozycji bazy stałej w makiecie podglądu. */
export const PINNED_PREVIEW_ID_PREFIX = 'pinned-';

/**
 * Statyczna makieta lewego sidebara tenanta zbudowana z roboczej hierarchii menu.
 * Renderowana wewnątrz `PreviewModal`. Odwzorowuje panel realnego sidebara klienta:
 * markę „Workflow365" z nazwą menu, menu (z roboczej hierarchii) i bazę stałą. Stopki
 * „Widok menu" nie ma — u klienta przełącznika menu w stopce już nie będzie (2026-09-23).
 */
export function MenuPreviewSidebar({
  hierarchy,
  modules,
  moduleName,
  selectedId,
  onSelectItem,
  hideNote = false,
  showPinnedBase = false,
  fullHeight = false,
}: MenuPreviewSidebarProps) {
  const { t } = useTranslation();
  const modulesByCode = new Map(modules.map((m) => [m.moduleCode, m]));

  // Baza stała jako syntetyczne MenuItem-y (id `pinned-<key>`) — z etykietą już
  // rozwiązaną, żeby prawy panel zunifikowanego podglądu miał co pokazać w nagłówku.
  const pinnedItems = useMemo<MenuItem[]>(
    () =>
      SYSTEM_ITEMS.filter((s) => PINNED_BASE_ITEM_KEYS.includes(s.itemKey)).map((def) => ({
        id: `${PINNED_PREVIEW_ID_PREFIX}${def.itemKey}`,
        type: 'system_item',
        itemKey: def.itemKey,
        label: t(def.labelI18nKey, def.defaultLabel),
      })),
    [t],
  );

  return (
    <div className={`flex flex-col items-center gap-3 ${fullHeight ? 'h-full' : ''}`}>
      {!hideNote && (
        <p className="max-w-md text-center text-xs text-text-muted">
          {t('admin.menuLayoutBuilder.preview.note')}
        </p>
      )}
      {/* data-skin — ta sama skórka co realny sidebar (kolory aktywnej pozycji z index.css). */}
      <aside
        className={`flex w-64 flex-col overflow-hidden rounded-2xl border border-border-secondary bg-bg-elevated shadow-lg ${
          fullHeight ? 'min-h-full' : ''
        }`}
        data-testid="menu-preview-sidebar"
        data-skin="antd"
      >
        <div className="flex flex-none flex-col gap-0.5 px-4 pb-[14px] pl-7 pt-5">
          <div className="text-[20px] font-semibold leading-[28px] tracking-[-0.3px] text-[#17171f]">
            Workflow365
          </div>
          <div className="truncate text-[12px] font-bold leading-[18px] text-[#2463eb]">
            {moduleName || t('tenant.sidebar.brandTagline')}
          </div>
        </div>
        <nav className="flex flex-1 flex-col px-4 pb-2 pt-2">
          {hierarchy.length === 0 ? (
            <div className="px-3 py-2 text-sm text-text-muted" data-testid="menu-preview-empty">
              {t('admin.menuLayoutBuilder.preview.empty')}
            </div>
          ) : (
            hierarchy.map((item) => (
              <PreviewNode
                key={item.id}
                item={item}
                modulesByCode={modulesByCode}
                selectedId={selectedId}
                onSelectItem={onSelectItem}
              />
            ))
          )}

          {/* Baza stała — lustro zony 3 realnego sidebara (`PinnedBaseNav`): zawsze
              pod menu modułu, niezależnie od hierarchii roboczej. */}
          {showPinnedBase && (
            <div
              className="mt-4 border-t border-border-secondary pt-3"
              data-testid="menu-preview-pinned-base"
            >
              {pinnedItems.map((item) => (
                <PreviewNode
                  key={item.id}
                  item={item}
                  modulesByCode={modulesByCode}
                  selectedId={selectedId}
                  onSelectItem={onSelectItem}
                />
              ))}
            </div>
          )}
        </nav>
      </aside>
    </div>
  );
}
